/**
 * Guest participant identity — the client half of migration 660.
 *
 * Lets someone weigh in on a session from a shared link without an account.
 * Every call goes through a SECURITY DEFINER RPC where the token lookup *is*
 * the authorization step and all scope is derived from the token row — the
 * proven pattern in this repo (`submit_via_embed_token`).
 *
 * ## What a guest can and cannot do
 *
 * A guest may contribute a response. Nothing else. Guests cannot open, close,
 * publish or ratify anything — build contract §2.3a / P-AGENT-1 require an
 * authenticated human principal for authority transitions, and this design sits
 * strictly below that line rather than weakening it.
 *
 * ## The guest secret
 *
 * A high-entropy value minted in the browser and kept in localStorage, scoped
 * per session. It is *not* a user account: it only proves "I am the same guest
 * who joined", so a refresh doesn't create a duplicate participant or lose a
 * submission. It is deliberately localStorage rather than a durable record —
 * losing it costs a guest nothing except re-entering their name, and storing
 * anything more would be collecting personal data we don't need.
 */

import { getSafeSupabaseClient } from './supabaseSync';

/** localStorage key for a per-session guest secret. */
function secretKey(token: string): string {
  return `pp_guest_secret:${token}`;
}

/** Mint (or reuse) this browser's opaque secret for one join link. */
export function getOrCreateGuestSecret(token: string): string {
  const key = secretKey(token);
  try {
    const existing = localStorage.getItem(key);
    if (existing && existing.length >= 20) return existing;
    const bytes = new Uint8Array(24);
    crypto.getRandomValues(bytes);
    const secret = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    localStorage.setItem(key, secret);
    return secret;
  } catch {
    // Private-browsing / storage-blocked: fall back to an in-memory secret.
    // The guest can still submit; they just won't be recognized on refresh.
    const bytes = new Uint8Array(24);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }
}

export interface GuestSession {
  guestId: string;
  sessionId: string;
  sessionName: string;
  displayName: string;
}

// ── The guest pass: the room this browser joined as a guest ────────────────

/**
 * After joining by link, a guest answers at the room's own address,
 * `/prioritization/sessions/:id/live`, the address a member lands on too
 * (roadmap S-8, ruling R1). The pass remembers, in this browser, the link and
 * the guest identity for that session, so the room's page can run for the
 * guest without a sign-in. It grants nothing by itself: every read and write
 * behind it still goes through the token-scoped SECURITY DEFINER functions
 * with the guest secret, exactly as on the link's page.
 */
export interface GuestPass extends GuestSession {
  token: string;
}

const PASS_PREFIX = 'pp_guest_pass:';

/** Remember that this browser joined `guest.sessionId` as a guest by `token`. */
export function saveGuestPass(token: string, guest: GuestSession): void {
  try {
    localStorage.setItem(`${PASS_PREFIX}${guest.sessionId}`, JSON.stringify({ ...guest, token }));
  } catch {
    // Storage blocked: the guest stays on the link's page, as before.
  }
}

function parsePass(raw: string | null): GuestPass | null {
  if (!raw) return null;
  try {
    const pass = JSON.parse(raw) as Partial<GuestPass>;
    if (pass.token && pass.guestId && pass.sessionId && pass.displayName) return pass as GuestPass;
  } catch {
    // A damaged entry is no pass.
  }
  return null;
}

/** The pass this browser holds for a session, if it joined it as a guest. */
export function readGuestPass(sessionId: string): GuestPass | null {
  try {
    return parsePass(localStorage.getItem(`${PASS_PREFIX}${sessionId}`));
  } catch {
    return null;
  }
}

/** The pass this browser holds for a join link, if it already joined by it. */
export function findGuestPassForToken(token: string): GuestPass | null {
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key?.startsWith(PASS_PREFIX)) continue;
      const pass = parsePass(localStorage.getItem(key));
      if (pass?.token === token) return pass;
    }
  } catch {
    // Storage blocked: no pass.
  }
  return null;
}

/** The room's own address, where members and guests both answer. */
export function guestRoomPath(sessionId: string): string {
  return `/prioritization/sessions/${sessionId}/live`;
}

/** The session a room address names: the live room, or the phone join the Board's code opens. */
export function roomAddressSessionId(pathname: string): string | null {
  const match = pathname.match(/^\/prioritization\/sessions\/([^/]+)\/(live|participate)\/?$/);
  return match ? match[1] : null;
}

/** The guest pass that lets this browser open `pathname` signed out, if any. */
export function guestPassForPath(pathname: string): GuestPass | null {
  const sessionId = roomAddressSessionId(pathname);
  return sessionId ? readGuestPass(sessionId) : null;
}

export interface GuestResult<T> {
  ok: boolean;
  data?: T;
  /** Human-readable, already safe to show — the RPCs raise plain messages. */
  error?: string;
}

/** Exchange a link token + a display name for a guest identity. */
export async function joinAsGuest(
  token: string,
  displayName: string,
): Promise<GuestResult<GuestSession>> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return { ok: false, error: 'Not connected.' };

  const secret = getOrCreateGuestSecret(token);
  const { data, error } = await supabase.rpc('join_session_with_guest_token', {
    p_token: token,
    p_display_name: displayName.trim(),
    p_guest_secret: secret,
  });

  if (error) return { ok: false, error: humanize(error.message) };
  const row = data as {
    guest_id: string;
    session_id: string;
    session_name: string;
    display_name: string;
  } | null;
  if (!row?.guest_id) return { ok: false, error: 'Could not join this session.' };

  return {
    ok: true,
    data: {
      guestId: row.guest_id,
      sessionId: row.session_id,
      sessionName: row.session_name,
      displayName: row.display_name,
    },
  };
}

/** Record this guest's response for one activity. Input only. */
export async function submitGuestResponse(
  token: string,
  toolSessionId: string,
  results: unknown,
): Promise<GuestResult<{ guestId: string }>> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return { ok: false, error: 'Not connected.' };

  const { data, error } = await supabase.rpc('submit_guest_response_with_token', {
    p_token: token,
    p_guest_secret: getOrCreateGuestSecret(token),
    p_tool_session_id: toolSessionId,
    p_results: results,
  });

  if (error) return { ok: false, error: humanize(error.message) };
  const row = data as { guest_id: string } | null;
  return { ok: true, data: { guestId: row?.guest_id ?? '' } };
}

/**
 * Read the activity behind a join link.
 *
 * The only way a guest can see anything: `tool_sessions` / `prioritization_sessions`
 * RLS is keyed on `auth.uid()`, which is NULL for an anonymous caller, so a
 * direct table read returns nothing. `get_guest_activity` (migration 740) is a
 * token-scoped SECURITY DEFINER read that returns the session name, the open
 * activity (or the group options and criteria) and — given this browser's guest
 * secret — this guest's own prior answer, and nothing else.
 */
export async function fetchGuestActivity(token: string): Promise<GuestResult<unknown>> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return { ok: false, error: 'Not connected.' };

  const { data, error } = await supabase.rpc('get_guest_activity', {
    p_token: token,
    p_guest_secret: getOrCreateGuestSecret(token),
  });

  if (error) return { ok: false, error: humanize(error.message) };
  return { ok: true, data };
}

/** One cell of a guest's group-session scorecard. */
export interface GuestGroupScore {
  item_id: string;
  criteria_id: string;
  score: number;
  note?: string | null;
}

/**
 * Record this guest's scores for a GROUP session.
 *
 * Group sessions have no `tool_sessions` row at all — GroupScoringView, reveal
 * and the results readers all work off `group_scores` — so this is a different
 * destination from `submitGuestResponse`, not a different encoding of the same
 * one. Migration 740 gave `group_scores` a `guest_participant_id` so a guest's
 * scores sit beside a member's and are counted by the same reveal.
 */
export async function submitGuestGroupScores(
  token: string,
  scores: GuestGroupScore[],
): Promise<GuestResult<{ guestId: string; scoresRecorded: number }>> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return { ok: false, error: 'Not connected.' };

  const { data, error } = await supabase.rpc('submit_guest_group_score', {
    p_token: token,
    p_guest_secret: getOrCreateGuestSecret(token),
    p_scores: scores,
  });

  if (error) return { ok: false, error: humanize(error.message) };
  const row = data as { guest_id?: string; scores_recorded?: number } | null;
  return {
    ok: true,
    data: { guestId: row?.guest_id ?? '', scoresRecorded: row?.scores_recorded ?? 0 },
  };
}

/**
 * Postgres prefixes raised exceptions; strip it so a participant sees the
 * sentence we wrote rather than a database artifact.
 */
function humanize(message: string): string {
  return message.replace(/^.*?(?:ERROR|error):\s*/i, '').trim() || 'Something went wrong.';
}
